import { openBrowserWASQLiteOPFSDatabase } from '@tanstack/browser-db-sqlite-persistence'
import type { BrowserWASQLiteDatabase } from '@tanstack/browser-db-sqlite-persistence'
import { startStartupMeasure } from './lib/startupPerformance'

const DATABASE_NAME = 'meldeportal.sqlite'
const AUTO_RESET_SESSION_KEY = 'meldeportal:auto-reset-local-db-attempted'
const DATABASE_VERIFIED_SESSION_KEY = 'meldeportal:local-db-verified'
const SCHEMA_VERSION_KEY = 'meldeportal:persisted-schema-version'
const FULL_RESYNC_SESSION_KEY = 'meldeportal:full-resync'

export function markLocalDatabaseFullResync(): void {
  try {
    sessionStorage.setItem(FULL_RESYNC_SESSION_KEY, '1')
  } catch {
    // sessionStorage can be unavailable (private mode); the UI just skips the extra copy.
  }
}

export function hasLocalDatabaseFullResync(): boolean {
  try {
    return sessionStorage.getItem(FULL_RESYNC_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

export function clearLocalDatabaseFullResync(): void {
  try {
    sessionStorage.removeItem(FULL_RESYNC_SESSION_KEY)
  } catch {
    // Ignore — a leftover flag only keeps the longer loading copy for this tab.
  }
}

interface SqliteQuickCheckRow {
  quick_check?: string
}

function isNotFoundError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'NotFoundError'
}

async function removeOpfsEntryIfExists(
  directory: FileSystemDirectoryHandle,
  name: string,
): Promise<void> {
  try {
    await directory.removeEntry(name, { recursive: true })
  } catch (error) {
    if (isNotFoundError(error)) {
      return
    }

    throw error
  }
}

async function deletePersistedDatabase(): Promise<void> {
  const rootDirectory = await navigator.storage.getDirectory()

  await Promise.all(
    ['', '-journal', '-wal'].map((suffix) =>
      removeOpfsEntryIfExists(rootDirectory, `${DATABASE_NAME}${suffix}`),
    ),
  )
}

function reloadAfterPersistedDatabaseReset(url?: URL): Promise<void> {
  return new Promise<void>((resolve) => {
    globalThis.addEventListener(
      'pagehide',
      () => {
        resolve()
      },
      { once: true },
    )

    if (url) {
      history.replaceState(history.state, '', url)
    }

    location.reload()
  })
}

/**
 * Drops the whole local database when the persisted schema version changes.
 *
 * The TanStack adapter's own `schemaVersion` reset only wipes collection *data* in-place; the
 * Electric stream offset survives, so synced collections resume incrementally instead of refetching
 * the full shape — leaving the UI empty until a manual reset. Deleting the OPFS file clears the
 * stream position too, forcing a clean full re-sync. Gated by a localStorage marker (separate from
 * OPFS) so it runs exactly once per version bump, and on the first load after this code ships it
 * heals any browser already wedged by an in-place schema reset.
 */
export async function resetPersistedDatabaseOnSchemaChange(version: number): Promise<void> {
  let storedVersion: string | null = null
  try {
    storedVersion = localStorage.getItem(SCHEMA_VERSION_KEY)
  } catch {
    return
  }

  if (storedVersion === String(version)) {
    return
  }

  await deletePersistedDatabase()
  markLocalDatabaseFullResync()

  try {
    localStorage.setItem(SCHEMA_VERSION_KEY, String(version))
  } catch {
    // If localStorage is unavailable the wipe still happened; it just repeats next load.
  }
}

export async function resetPersistedDatabaseFromUrl(): Promise<void> {
  const url = new URL(location.href)
  if (!url.searchParams.has('resetLocalDb')) {
    return
  }

  url.searchParams.delete('resetLocalDb')

  try {
    await deletePersistedDatabase()
    markLocalDatabaseFullResync()
    await reloadAfterPersistedDatabaseReset(url)
  } catch (error) {
    console.error('Failed to reset local TanStack DB cache', error)
  }
}

function isPersistedDatabaseCorruptionError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : ''
  const normalizedMessage = message.toLowerCase()

  return [
    'sqlite_corrupt',
    'database disk image is malformed',
    'file is not a database',
    'not a database',
    'malformed database',
    'database malformed',
    'sqlite corruption',
    'sqlite database is corrupt',
    'quick_check failed',
  ].some((needle) => normalizedMessage.includes(needle))
}

async function assertPersistedDatabaseReadable(database: BrowserWASQLiteDatabase): Promise<void> {
  // Emit a `meldeportal:db-quick-check` performance measure (visible in the Performance panel and
  // pickable up by telemetry via PerformanceObserver) plus a console line, so the real cost of the
  // once-per-session integrity scan is observable as the cache grows.
  performance.mark('meldeportal:db-quick-check:start')

  const rows = await database.execute<SqliteQuickCheckRow>('PRAGMA quick_check')

  const measure = performance.measure(
    'meldeportal:db-quick-check',
    'meldeportal:db-quick-check:start',
  )

  let sizeNote = ''
  try {
    const pageCountRows = await database.execute<Record<string, number>>('PRAGMA page_count')
    const pageSizeRows = await database.execute<Record<string, number>>('PRAGMA page_size')
    const [pageCount] = Object.values(pageCountRows[0] ?? {})
    const [pageSize] = Object.values(pageSizeRows[0] ?? {})
    if (typeof pageCount === 'number' && typeof pageSize === 'number') {
      sizeNote = ` over ${((pageCount * pageSize) / 1_048_576).toFixed(1)} MB`
    }
  } catch {
    // Size reporting is best-effort; never let it mask the readability check.
  }

  // eslint-disable-next-line no-console -- intentional one-per-session perf signal
  console.info(`[persistence] SQLite quick_check took ${measure.duration.toFixed(1)}ms${sizeNote}`)

  const firstValue = rows[0]?.quick_check

  if (firstValue !== 'ok') {
    throw new Error(`SQLite quick_check failed: ${String(firstValue)}`)
  }
}

export async function openReadablePersistedDatabase(
  openDatabase = openBrowserWASQLiteOPFSDatabase,
): Promise<BrowserWASQLiteDatabase> {
  const finish = startStartupMeasure('persistence:open')
  let database: BrowserWASQLiteDatabase
  try {
    database = await openPersistedDatabase(openDatabase)
  } finally {
    finish()
  }

  // `quick_check` scans the whole database, so it adds latency proportional to cache size. Run it
  // only once per browser session (first load) rather than on every navigation that imports a
  // collection — a session that opened cleanly stays trusted; mid-session faults still surface as
  // read errors and trip the corruption auto-reset.
  if (getSessionValue(DATABASE_VERIFIED_SESSION_KEY) !== '1') {
    try {
      await assertPersistedDatabaseReadable(database)
    } catch (error) {
      try {
        await database.close?.()
      } catch (closeError) {
        console.error('Failed to close unreadable TanStack DB cache', closeError)
      }

      throw error
    }

    setSessionValue(DATABASE_VERIFIED_SESSION_KEY, '1')
  }

  return database
}

function getSessionValue(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function setSessionValue(key: string, value: string): boolean {
  try {
    sessionStorage.setItem(key, value)
    return true
  } catch {
    // Storage restrictions must not turn an opened OPFS database into a leaked connection.
    return false
  }
}

async function openPersistedDatabase(
  openDatabase: typeof openBrowserWASQLiteOPFSDatabase,
): Promise<BrowserWASQLiteDatabase> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await openDatabase({ databaseName: DATABASE_NAME })
    } catch (error) {
      // The adapter terminates its failed worker. A reload can briefly race the previous
      // worker's file-handle release; retry opening without deleting a potentially valid cache.
      const retryable =
        error instanceof Error &&
        error.name === 'OPFSWorkerRequestError' &&
        error.message === 'sqlite3_open_v2'
      if (!retryable || attempt >= 2) throw error
      const finish = startStartupMeasure('persistence:open-retry')
      await new Promise((resolve) => {
        setTimeout(resolve, 100 * (attempt + 1))
      })
      finish()
    }
  }
}

/**
 * Triggers a full reset of the local cache from anywhere in the app (e.g. a profile-menu button).
 * Navigates to the same URL with `?resetLocalDb`, which `resetPersistedDatabaseFromUrl()` handles on
 * the next load — before the database is opened, so it avoids the OPFS `NoModificationAllowedError`
 * a delete would hit while the wa-sqlite worker holds the file handles.
 */
export function requestLocalCacheReset(): void {
  const url = new URL(location.href)
  url.searchParams.set('resetLocalDb', '')
  sessionStorage.removeItem(DATABASE_VERIFIED_SESSION_KEY)
  location.href = url.toString()
}

export async function resetCorruptPersistedDatabaseOnce(error: unknown): Promise<boolean> {
  if (!isPersistedDatabaseCorruptionError(error)) {
    return false
  }

  if (getSessionValue(AUTO_RESET_SESSION_KEY) === '1') {
    return false
  }

  // Without a durable guard, a reload could repeatedly reset the cache in a restricted browser.
  if (!setSessionValue(AUTO_RESET_SESSION_KEY, '1')) return false

  try {
    await deletePersistedDatabase()
    await reloadAfterPersistedDatabaseReset()
    return true
  } catch (resetError) {
    console.error('Failed to reset corrupt local TanStack DB cache', resetError)
    return false
  }
}
